/**
 * A KPI's light and its trend (docs/performance.md; issue #896): the light is derived from the latest
 * reading against the target and its AMBER band, the trend from the latest reading against the one before
 * it — direction and magnitude, never a verdict — and overdue from the last reading's age against the
 * KPI's cadence. Readings sort newest first, the order the portal, the API and the MCP server all list
 * them in (apps/api/src/kpis/kpis.controller.spec.ts, apps/mcp/src/tool-catalog.spec.ts,
 * apps/portal/src/components/KpiTargetCard.spec.ts).
 *
 * The read contract HQ's KPI board is built from (#898) lives here too, at the foot of the file: the API
 * and the MCP server hand back that same shape and word it through this file's one formatter, so "what is
 * RED?" reads the same whether it comes from HQ, the API, or an external agent.
 *
 * Pure and framework-free so the portal, the API and the MCP server derive the light, the trend and
 * overdue the same way instead of three implementations drifting apart — the "one shared contract"
 * docs/performance.md asks for. Nothing here is stored: a reading is the only thing kept, and the light,
 * the trend and overdue are read off the readings every time they are shown.
 */

/** Which way is good: at least a floor, or at most a ceiling (docs/performance.md, Targets and the light). */
export type KpiDirection = 'at_least' | 'at_most'

/**
 * Where a KPI's readings come from (docs/performance.md, Setting a KPI): a task that records them with the tools
 * bound to it, or a person who enters them by hand. A KPI is signed off only once its source is set.
 */
export type KpiSourceKind = 'task' | 'person'

/** Every source a KPI may name, for the surfaces that validate one. */
export const KPI_SOURCE_KINDS: readonly KpiSourceKind[] = ['task', 'person']

/** A KPI's target, as it is set (docs/performance.md, Setting a KPI). */
export interface KpiTarget {
    value: number
    direction: KpiDirection
    /** Distance from `value` that still reads AMBER rather than RED. 0 means no band: on target, or RED. */
    amberBand: number
    /** AMBER even though the latest reading is on target, when it is worse than the previous reading. */
    worsensOnTarget?: boolean
}

/** One reading of a KPI: the value, when it was taken, and who or what took it. */
export interface KpiReading {
    value: number
    /** ISO 8601. */
    takenAt: string
    recordedBy: string
}

/** A KPI read against its target: green on target, amber at risk, red off target. Matches packages/ui's KpiRag. */
export type KpiRag = 'green' | 'amber' | 'red'

/**
 * The number's own direction and size against the previous reading. Never a verdict: for an "at most" KPI,
 * a fall is good, but the trend still only ever reports the fall.
 */
export interface KpiTrend {
    direction: 'up' | 'down' | 'flat'
    /** Magnitude only, e.g. 5 for a 5% move. */
    percent: number
}

/** Whether a KPI's reading is overdue for its cadence, with the last light and reading time to show while it is. */
export interface KpiOverdue {
    overdue: boolean
    lastLight: KpiRag | null
    lastTakenAt: string | null
}

/** Milliseconds in a minute, so a cadence in minutes can be compared against two instants. */
const MINUTE_MS = 60_000

/**
 * `readings`, newest first — the order the portal, the API and the MCP server list them in
 * (`GET /v1/kpis/:kpiId/readings`, `kpi.list_readings`). Generic in the reading, so a caller with rows that
 * carry more than the contract (the API's stored readings carry an id) keeps them through the sort.
 *
 * Sorted by `takenAt`, not by when the row was written: a correction recorded today for a reading taken last
 * week belongs where it was taken. The input is never mutated — callers pass props and query results
 * straight in.
 */
export function sortReadingsNewestFirst<Reading extends KpiReading>(readings: readonly Reading[]): Reading[] {
    return [...readings].sort((left, right) => instant(right.takenAt) - instant(left.takenAt))
}

/**
 * The light for `target`'s latest reading among `readings`, or null when there is no target — a KPI with no
 * target records readings but shows no light (issue #896 acceptance criterion) — and null when nothing has
 * been read yet, since a light with no reading behind it would be a verdict on no evidence.
 *
 * GREEN is on target: at or above an "at least" target, at or below an "at most" one. AMBER is inside the
 * band on the wrong side of it, and RED is everything beyond that. A KPI that asks for it also reads AMBER
 * while it is on target but moving the wrong way against its previous reading — the early warning
 * docs/performance.md calls the on-target-but-worsening rule.
 */
export function kpiLight(target: KpiTarget | null, readings: readonly KpiReading[]): KpiRag | null {
    if (!target) {
        return null
    }
    const [latest, previous] = sortReadingsNewestFirst(readings)
    if (!latest) {
        return null
    }
    const rag = ragOf(target, latest.value)
    if (rag === 'green' && target.worsensOnTarget && previous && worsened(target.direction, latest, previous)) {
        return 'amber'
    }
    return rag
}

/** Where `value` sits against `target`: on it, inside its band, or beyond. */
function ragOf(target: KpiTarget, value: number): KpiRag {
    const band = Math.abs(target.amberBand ?? 0)
    if (target.direction === 'at_least') {
        if (value >= target.value) {
            return 'green'
        }
        return value >= target.value - band ? 'amber' : 'red'
    }
    if (value <= target.value) {
        return 'green'
    }
    return value <= target.value + band ? 'amber' : 'red'
}

/** Whether `latest` moved away from what `direction` wants, against `previous`. */
function worsened(direction: KpiDirection, latest: KpiReading, previous: KpiReading): boolean {
    return direction === 'at_least' ? latest.value < previous.value : latest.value > previous.value
}

/**
 * The latest reading's direction and size against the one before it, or null with fewer than two readings —
 * one reading has nothing to move against. Independent of any target: the arrow is the number's own
 * movement, never read as good or bad, so an "at most" KPI that fell reports the fall and leaves the
 * judgement to the light.
 *
 * The size is the percentage of the previous reading, rounded to one decimal place so `↑ 5%` reads as it is
 * written. A previous reading of zero has no percentage to give — any move off zero is infinite — so the
 * direction is reported with a size of 0.
 */
export function kpiTrend(readings: readonly KpiReading[]): KpiTrend | null {
    const [latest, previous] = sortReadingsNewestFirst(readings)
    if (!latest || !previous) {
        return null
    }
    const change = latest.value - previous.value
    if (change === 0) {
        return { direction: 'flat', percent: 0 }
    }
    const percent = previous.value === 0 ? 0 : Math.round((Math.abs(change) / Math.abs(previous.value)) * 1000) / 10
    return { direction: change > 0 ? 'up' : 'down', percent }
}

/**
 * Whether the latest of `readings` is older than `cadenceMinutes` as of `now`, carrying the last light and
 * when it was taken to show meanwhile — an overdue KPI says so instead of passing an old light off as
 * current. Overdue the moment the cadence window elapses with no new reading, with no grace period on top.
 *
 * Null when the KPI has no cadence to be late for or no reading to be late since. The cadence, not the target,
 * is what makes a reading due (#1696, CTO decision 4): a KPI measuring a baseline with no target yet is still
 * late once its cadence elapses, and carries a null last light meanwhile, since it has no light to go stale.
 */
export function kpiOverdue(
    target: KpiTarget | null,
    cadenceMinutes: number | null,
    readings: readonly KpiReading[],
    now: Date,
): KpiOverdue | null {
    const [latest] = sortReadingsNewestFirst(readings)
    if (!cadenceMinutes || !latest) {
        return null
    }
    const age = now.getTime() - instant(latest.takenAt)
    return {
        overdue: age > cadenceMinutes * MINUTE_MS,
        lastLight: kpiLight(target, readings),
        lastTakenAt: latest.takenAt,
    }
}

/**
 * Overdue as a listing and HQ's board both say it, as one flag: stale past its cadence (`kpiOverdue`), or never
 * read at all while there is something a reading is due against. A cadence makes a reading due whether or not
 * there is a target, and a target with no cadence keeps the rule it always had, overdue only until it is first read.
 * A KPI with neither a cadence nor a target is never overdue, not even before its first reading, so a baseline KPI
 * created today does not read as late on the day it is created (#1696, CTO decision 4).
 */
export function kpiReadingOverdue(
    target: KpiTarget | null,
    cadenceMinutes: number | null,
    readings: readonly KpiReading[],
    now: Date,
): boolean {
    const overdue = kpiOverdue(target, cadenceMinutes, readings, now)
    if (overdue) {
        return overdue.overdue
    }
    return readings.length === 0 && (target !== null || Boolean(cadenceMinutes))
}

/** `takenAt` as milliseconds, with an unparseable one sorting oldest rather than throwing. */
function instant(takenAt: string): number {
    const parsed = new Date(takenAt).getTime()
    return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * HQ's KPI board (#898): the read contract the API and the MCP server share — both hand back this same
 * shape, and both turn it into words through `describeKpiBoard` below. The light, the trend and overdue in
 * a row are the ones derived above, so a board never carries a second opinion about a KPI's colour.
 */

/** One KPI's current reading, as a board row shows it: its light, its trend, and when it was last read. */
export interface KpiReadingView {
    id: string
    name: string
    /** The light off the latest reading, or null when there is none to judge: no reading yet, or no target. */
    rag: KpiRag | null
    trend: 'up' | 'down' | 'flat'
    /** ISO 8601, or null when the KPI has never been read: overdue either way. */
    lastReadAt: string | null
    /** How long a reading holds before it counts as overdue, in days. */
    cadenceDays: number
    /** Stale past its own cadence, or never read at all — worth saying even when the light is green. */
    overdue: boolean
    /**
     * True on a KPI still waiting for a person to sign it off, which only a team's page lists (the board never does):
     * no run sees it or records a reading for it until then, so it has no light and is never overdue.
     */
    pending?: boolean
}

/** One team's row on HQ and the teams list: its key KPI, or none set yet. */
export interface TeamKpiBoardRow {
    teamId: string
    teamName: string
    key: KpiReadingView | null
}

/** What the API's KPI-read endpoint and the MCP server's tool both return for one enterprise. */
export interface KpiBoard {
    enterprise: KpiReadingView[]
    teams: TeamKpiBoardRow[]
}

/**
 * Names every RED and AMBER KPI — and an overdue one of any light, or of none — enterprise KPIs first, then each team's key
 * KPI by team name; a team with no key KPI is named too, never left silent. The API's KPI-read endpoint
 * and the MCP server's tool both call this, so chat, an external agent, and HQ's own board never disagree
 * about a KPI's colour. Pure: the caller already has the board.
 *
 * With nothing to name, the all-clear only claims what is true: a KPI with no target yet is not named as a problem
 * (#1696, CTO decision 8), but it is not on target either, so the sentence says how many have no target yet rather
 * than calling every KPI on target — the answer Blockers' `no_target` gives for the same board.
 */
export function describeKpiBoard(board: KpiBoard): string {
    const lines: string[] = []
    const shown = [...board.enterprise]
    for (const reading of board.enterprise) {
        if (worthNaming(reading)) {
            lines.push(describeReading(reading))
        }
    }
    for (const row of board.teams) {
        if (row.key) {
            shown.push(row.key)
            if (worthNaming(row.key)) {
                lines.push(`${row.teamName} — ${describeReading(row.key)}`)
            }
        } else {
            lines.push(`${row.teamName}: ${NO_KEY_KPI}`)
        }
    }
    if (lines.length > 0) {
        return lines.join('\n')
    }
    return allClear(shown.length, shown.filter(awaitsTarget).length)
}

/** What a board with nothing worth naming reads as, of `shown` KPIs of which `noTarget` have no target yet. */
function allClear(shown: number, noTarget: number): string {
    if (noTarget === 0) {
        return 'Every KPI is on target and read on time.'
    }
    if (noTarget === shown) {
        return 'No KPI has a target yet, and none is overdue.'
    }
    const have = noTarget === 1 ? '1 KPI has' : `${noTarget} KPIs have`
    return `Every KPI with a target is on target and read on time; ${have} no target yet.`
}

/**
 * Whether a row left unnamed is one with no target yet. A row carries no target, but nothing else leaves a row with
 * no light and not overdue: a KPI with a target and a reading has a light, and one with a target and no reading is
 * overdue until it is first read (`kpiReadingOverdue`).
 */
function awaitsTarget(reading: KpiReadingView): boolean {
    return reading.rag === null && !reading.overdue
}

/** What a team with no key KPI reads as, here and on HQ: never a blank, never silence. */
export const NO_KEY_KPI = 'No key KPI set' as const

/** The light in the words an agent answering "what is RED?" is expected to use. */
const RAG_WORD: Readonly<Record<KpiRag, string>> = { red: 'RED', amber: 'AMBER', green: 'GREEN' }

const TREND_WORD: Readonly<Record<KpiReadingView['trend'], string>> = {
    up: 'trending up',
    down: 'trending down',
    flat: 'flat',
}

/**
 * Off target, at risk, or stale: the readings worth naming. A KPI on target and read on time is left out,
 * so an answer to "what is RED?" is the list of what is wrong rather than the whole board again. A KPI with
 * no light (no target yet, or nothing read) is not wrong either, so it is named only when it is overdue
 * (#1696, CTO decision 8).
 */
function worthNaming(reading: KpiReadingView): boolean {
    if (reading.overdue) {
        return true
    }
    return reading.rag === 'red' || reading.rag === 'amber'
}

/** One reading in words: its name, its light, its trend, and when it was last read (or that it never was). */
function describeReading(reading: KpiReadingView): string {
    const read = reading.lastReadAt ? `last read ${reading.lastReadAt.slice(0, 10)}` : 'never read'
    const overdue = reading.overdue ? `, overdue (read every ${reading.cadenceDays} days)` : ''
    // A KPI with nothing to judge has no light, and so no trend worth saying either.
    const light = reading.rag ? `${RAG_WORD[reading.rag]}, ${TREND_WORD[reading.trend]}` : 'no light yet'
    return `${reading.name}: ${light}, ${read}${overdue}`
}
